import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import MapView from '@arcgis/core/views/MapView';
import { MapViewInputTask } from 'cd-eft/components/Design/BreaklineTools/framework/MapViewInputTask';
import { GroundPoint } from 'cd-eft/components/Design/BreaklineTools/model/GroundPoint';
import { SurfaceDef } from 'cd-eft/components/Design/BreaklineTools/model/SurfaceDef';
import { esriGeometryEngine, Graphic, Point, SimpleMarkerSymbol } from 'cd-eft/utils/esriTypes';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import stores from 'cd-eft/models/Stores';

const setStore = stores.AppStore.surveyEngineeringStore;
export class EditTinablePointsInputTask extends MapViewInputTask<GroundPoint> {
  private readonly surfaceDef: SurfaceDef;
  private allGroundPoints: GroundPoint[] = [];
  private allGroundPointsMP: Multipoint = new Multipoint();
  private pointerMovePoint?: Point;
  private feedbackLayer = new GraphicsLayer({ title: 'feedbackLayer', listMode: 'hide' });
  private track_circle = new SimpleMarkerSymbol({
    color: undefined,
    size: 21,
    style: 'circle',
    outline: { color: 'red', width: 5 },
  });

  constructor(mapView: MapView, surfaceDef: SurfaceDef) {
    super(mapView);
    this.surfaceDef = surfaceDef;
  }

  protected onSetup(): void {
    super.onSetup();
    this.cursor = 'pointer';

    this.allGroundPoints = this.surfaceDef.getAllGroundPointEditables(setStore.currentSurvey.surveyId, setStore.pointDescriptionFilter);
    const multipoint: Multipoint = new Multipoint({ spatialReference: this.mapView.spatialReference });
    for (const point of this.allGroundPoints) {
      multipoint.addPoint(point.generateGeometry(this.mapView.spatialReference));
    }
    this.allGroundPointsMP = multipoint;
    this.mapView.map.add(this.feedbackLayer);
  }

  protected onCleanup(): void {
    super.onCleanup();

    this.mapView.map.remove(this.feedbackLayer);
    this.feedbackLayer.removeAll();

    this.pointerMovePoint = undefined;

    this.allGroundPoints = [];
    this.allGroundPointsMP = new Multipoint();
  }

  protected async onImmediateClick(e: __esri.ViewImmediateClickEvent): Promise<void> {
    super.onImmediateClick(e);

    const groundPoint = this.findNearestGroundPoint(e.mapPoint); 

    if (groundPoint) {
      for (const survey of this.surfaceDef.surveyEditables) {
        for (const groundPt of survey.groundPoints) {
          if (groundPt.id === groundPoint.id) {
            const value = !groundPoint.tinable;
            groundPt.tinable = value ? 1 : 0;

            const surface = setStore.triggerSurfaceDesignerVm?.surfaceDef;

            const pointProcessingType = await surface?.autoRegenerateBorder(groundPoint.id, value);
            if (pointProcessingType === "border and modify") {
              setStore.triggerSurfaceDesignerVm.addBorderTool?.reDrawBorder();
            }

            if (!setStore.isImportingNewSurvey) {
              setStore.triggerSurfaceDesignerVm?.surfaceDef?.logPendingEdits();
              setStore.triggerSurfaceDesignerVm?.drawSurveyPoints();
              setStore.triggerBreaklineToolboxVM?.drawGroundPoints();
            }

            break
          }
        }
      }
    }
  }

  protected async onPointerMove(e: __esri.ViewPointerMoveEvent): Promise<void> {
    super.onPointerMove(e);

    this.pointerMovePoint = this.mapView.toMap(e);
    this.updateFeedbackLayer();
  }

  private updateFeedbackLayer(): void {
    //clear the layer
    this.feedbackLayer.removeAll();
    
    if (!this.pointerMovePoint) {
      return;
    }

    const groundPoint = this.findNearestGroundPoint(this.pointerMovePoint);
    
    if (groundPoint) {
      const nextPoint = groundPoint.generateGeometry(this.mapView.spatialReference);
      const nextPointGraphic = new Graphic({
        geometry: nextPoint,
        symbol: this.track_circle,
      });
      this.feedbackLayer.add(nextPointGraphic);
    }
  }

  private findNearestGroundPoint(mapPoint: Point): GroundPoint {
    const findResult = esriGeometryEngine.nearestVertex(this.allGroundPointsMP, mapPoint);
    let result;
    if(findResult.distance < 11) {
      result = this.allGroundPoints[findResult.vertexIndex];
    }
    return result;
  }
}